import { Table, Dropdown } from "antd";
import "@/assets/styles/custom_table.css";
import PropTypes from "prop-types";
import Column from "antd/es/table/Column";
import { Link } from "react-router-dom";
import Avatar from "@/components/common/Avatar";
import { useState } from "react";
import CustomMoreOptions from "@/components/specific/Custom_More_Options";
import Delete from "@/components/common/alerts/Delete";
import { useSelector } from "react-redux";
import { selectSearchTerm } from "@/store/redux/slices/search/search_slice";

const DonationsTable = ({
  setSelectedEvents,
  data,
  isLoading,
  onDelete,
  onModify,
}) => {
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [selectedDonation, setSelectedDonation] = useState(null);
  const globalSearchTerm = useSelector(selectSearchTerm) || "";

  // Return null if there's no data or loading
  if (!data || data.length === 0 || isLoading) {
    return null;
  }

  // rowSelection object indicates the need for row selection
  const rowSelection = {
    onChange: (selectedRowKeys) => {
      setSelectedEvents(selectedRowKeys);
    },
    getCheckboxProps: (record) => ({
      disabled: record.name === "Disabled User",
      name: record.name,
    }),
  };

  // Apply global search filter
  const filteredData = globalSearchTerm
    ? data.filter(
        (item) =>
          item.title?.toLowerCase().includes(globalSearchTerm.toLowerCase()) ||
          item.amount?.toLowerCase().includes(globalSearchTerm.toLowerCase()) ||
          item.category
            ?.toLowerCase()
            .includes(globalSearchTerm.toLowerCase()) ||
          item.status?.toLowerCase().includes(globalSearchTerm.toLowerCase())
      )
    : data;

  const handleMenuClick = (menuItem, record) => {
    if (menuItem.key === "delete") {
      setSelectedDonation(record);
      setDeleteModalVisible(true);
    } else if (menuItem.key === "modify") {
      onModify(record.id);
    }
  };

  const handleDeleteConfirm = () => {
    if (selectedDonation) {
      onDelete(selectedDonation.id, selectedDonation.title);
      setDeleteModalVisible(false);
      setSelectedDonation(null);
    }
  };

  return (
    <>
      <Table
        rowSelection={{
          type: "checkbox",
          ...rowSelection,
        }}
        dataSource={filteredData}
        loading={isLoading}
        rowKey="id">
        <>
          <Column
            title="Donator"
            dataIndex="title"
            key="title"
            render={(_, { id, title, imgUrl }) => (
              <Link
                to={`/dashboard/financial/transaction/${id}`}
                className="flex items-center gap-3 font-bold text-[15px]">
                <div className="">
                  <Avatar src={imgUrl} alt={title || "Donator"} size={40} />
                </div>
                <p className="text-regular font-bold truncate w-[200px]">
                  {title}
                </p>
              </Link>
            )}
          />
          <Column
            title="Amount"
            dataIndex="amount"
            key="amount"
            render={(_, { id, amount }) => (
              <Link
                to={`/dashboard/financial/transaction/${id}`}
                className="font-bold text-[15px]">
                <p>{amount}</p>
              </Link>
            )}
          />
          <Column
            title="Channel"
            dataIndex="category"
            key="category"
            render={(_, { id, category }) => (
              <Link
                to={`/dashboard/financial/transaction/${id}`}
                className="text-[15px] font-bold">
                <p className="text-regular font-bold">{category}</p>
              </Link>
            )}
          />
          <Column
            title="Date and Time"
            key="date"
            dataIndex="date"
            render={(_, { id, date, time }) => (
              <Link
                to={`/dashboard/financial/transaction/${id}`}
                className="text-xs">
                <p className="text-small whitespace-nowrap">
                  {date} {time}
                </p>
              </Link>
            )}
          />
          <Column
            title="Status"
            key="status"
            dataIndex="status"
            render={(_, { id, status }) => (
              <Link
                to={`/dashboard/financial/transaction/${id}`}
                className="flex items-center gap-1">
                <div className="w-2 h-2 rounded-full bg-primary" />
                <p className="text-primary font-bold text-small">{status}</p>
              </Link>
            )}
          />
          <Column
            title=""
            dataIndex="options"
            key="options"
            render={(_, record) => (
              <Dropdown
                menu={{
                  items: [
                    {
                      key: "modify",
                      label: "Modify",
                      onClick: (e) => {
                        e.domEvent.stopPropagation();
                        handleMenuClick({ key: "modify" }, record);
                      },
                    },
                    {
                      key: "delete",
                      label: "Delete",
                      onClick: (e) => {
                        e.domEvent.stopPropagation();
                        handleMenuClick({ key: "delete" }, record);
                      },
                    },
                  ],
                }}
                trigger={["click"]}>
                <CustomMoreOptions
                  variant="filled"
                  onClick={(e) => e.stopPropagation()}
                  color="black"
                  orientation="vertical"
                  size="small"
                />
              </Dropdown>
            )}
          />
        </>
      </Table>

      <Delete
        isVisible={deleteModalVisible}
        onClose={() => setDeleteModalVisible(false)}
        onProceed={handleDeleteConfirm}
        header="Delete Donation?"
        paragraph={
          selectedDonation
            ? `Are you sure you want to delete the donation from "${selectedDonation.title}" from your existing list?`
            : ""
        }
        proceedButtonText="Delete"
        cancelButtonText="Cancel"
      />
    </>
  );
};

DonationsTable.propTypes = {
  setSelectedEvents: PropTypes.func,
  data: PropTypes.array,
  isLoading: PropTypes.bool,
  onDelete: PropTypes.func,
  onModify: PropTypes.func,
};

export default DonationsTable;
